import { useEffect, useState } from 'react';
import Grid from '@mui/material/Unstable_Grid2';
import { Box, Button, Typography, TextField } from '@mui/material';
import Loading from '@/component/mui/Backdrop';
import { toast } from 'react-toastify';
import { SUCCESS } from '@/lib/common/name-space';
import { packBigNumbers } from '@/lib/BigNumbers';
import Header from '@/component/Header';
import { Card, CardContent, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { createBetPoint } from '@/api/bet/create';
import OtpDialog from '@/component/otp/OtpDialog';
// import { createBetPoint } from '@/api/bet/create';

// 베팅 포인트 입력 타입 정의
interface BetPointInputType {
  id: number;
  address: string;
  amount: string;
}

export default function AddBetPoint() {
  const [betPoints, setBetPoints] = useState<BetPointInputType[]>([{ id: 0, address: '', amount: '' }]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOtpDialogOpen, setIsOtpDialogOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<any>(null);
  const navigate = useNavigate();

  const handleAddInputs = () => {
    const newId = betPoints.length + 1;
    setBetPoints([
      ...betPoints,
      {
        id: newId,
        address: '',
        amount: '',
      },
    ]);
  };

  const handleDelete = () => {
    if (betPoints.length > 1) {
      setBetPoints((prevBetPoints) => prevBetPoints.slice(0, -1));
    }
  };

  const handleInputChange = (id: number, field: 'address' | 'amount', value: string) => {
    setBetPoints((prevBetPoints) =>
      prevBetPoints.map((betPoint) => (betPoint.id === id ? { ...betPoint, [field]: value } : betPoint)),
    );
  };

  const handleOtpSuccess = async () => {
    setIsLoading(true);
    try {
      // OTP 인증 성공 시 베팅 포인트 생성 진행
      const re = await createBetPoint(pendingPayload);
      if (re.status !== SUCCESS) {
        toast.error('베팅 포인트 생성에 실패했습니다.');
        return;
      }

      toast.success('베팅 포인트가 성공적으로 생성되었습니다.');
      // 초기 상태로 리셋
      setBetPoints([{ id: 0, address: '', amount: '' }]);
      setIsOtpDialogOpen(false);
      setPendingPayload(null);
    } catch (e) {
      console.log(e);
      toast.error('베팅 포인트 생성 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpClose = () => {
    setIsOtpDialogOpen(false);
    setPendingPayload(null);
  };

  const handleCreate = async () => {
    // 필수 입력값 검증
    const validBetPoints = betPoints.filter((item) => item.address.trim() !== '' && item.amount.trim() !== '');

    if (validBetPoints.length === 0) {
      toast.error('추가할 베팅 포인트 데이터가 없습니다.');
      return;
    }

    // 주소 형식 검증 (간단한 이더리움 주소 형식)
    const addressRegex = /^0x[a-fA-F0-9]{40}$/;
    for (const betPoint of validBetPoints) {
      if (!addressRegex.test(betPoint.address)) {
        toast.error('올바른 지갑 주소 형식이 아닙니다.');
        return;
      }
    }

    // payload 생성 및 OTP 인증 다이얼로그 열기
    const payload = validBetPoints.map((item) => ({
      address: item.address,
      amount: item.amount,
    }));

    setPendingPayload(payload);
    setIsOtpDialogOpen(true);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          overflow: 'auto',
          flex: 1,
          flexDirection: 'column',
          gap: { xs: '16px', sm: '0px' },
          height: '100vh',
          maxHeight: '100vh',
        }}
      >
        <Card>
          <Header title="베팅 포인트 생성" />
          <Divider />
          <Box mb={2} />
          <CardContent sx={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>
            <Box p={2}>
              <Grid container spacing={3} mb={2}>
                <Grid xs={6} sm={6}>
                  <Typography variant="h6">지갑 주소</Typography>
                </Grid>
                <Grid xs={6} sm={6}>
                  <Typography variant="h6">포인트</Typography>
                </Grid>
              </Grid>
            </Box>
            <Box p={2}>
              {betPoints.map((betPoint: BetPointInputType) => (
                <Grid container spacing={2} key={betPoint.id} sx={{ mb: 2 }}>
                  <Grid xs={6} sm={6}>
                    <TextField
                      fullWidth
                      label="지갑 주소"
                      value={betPoint.address}
                      onChange={(e) => handleInputChange(betPoint.id, 'address', e.target.value)}
                      placeholder="0x..."
                      size="small"
                    />
                  </Grid>
                  <Grid xs={6} sm={6}>
                    <TextField
                      fullWidth
                      label="포인트"
                      value={betPoint.amount}
                      onChange={(e) => handleInputChange(betPoint.id, 'amount', e.target.value)}
                      placeholder="0"
                      size="small"
                      type="number"
                    />
                  </Grid>
                </Grid>
              ))}
              <Grid container spacing={2}>
                <Grid xs={6}>
                  <Button fullWidth variant="outlined" color="secondary" onClick={handleAddInputs}>
                    추가
                  </Button>
                </Grid>
                <Grid xs={6}>
                  <Button fullWidth variant="outlined" color="secondary" onClick={handleDelete}>
                    삭제
                  </Button>
                </Grid>
                <Grid xs={12}>
                  <Button fullWidth variant="contained" color="primary" onClick={handleCreate}>
                    베팅 포인트 지급
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Box>
      <Loading isLoading={isLoading} />

      {/* OTP 인증 다이얼로그 */}
      <OtpDialog
        open={isOtpDialogOpen}
        onClose={handleOtpClose}
        onSuccess={handleOtpSuccess}
        title="베팅 포인트 지급 인증"
        description="베팅 포인트 지급을 위해 OTP 인증이 필요합니다."
        isLoading={isLoading}
      />
    </>
  );
}
